import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { Download, FileText, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import useJobProgress from '../../../hooks/useJobProgress'
import { CREATE_PHOTO_REPORT } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import { DEFAULT_REPORT_FIELDS, REPORT_FIELDS, REPORT_MAX_PHOTOS, ReportSource, reportInput, reportLanguage } from '../lib/report'
import type { PhotoFilterInput } from '../types'

interface Props {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedIds: string[]
  filter: PhotoFilterInput
  matchCount: number | null
}

const PER_ROW = [1, 2, 3, 4, 5]
const POLL_MAX_MS = 5 * 60 * 1000

const PhotoReportDialog: React.FC<Props> = ({ projectId, open, onOpenChange, selectedIds, filter, matchCount }) => {
  const { t, i18n } = useTranslation()
  const [source, setSource] = useState<ReportSource>('filter')
  const [title, setTitle] = useState('')
  const [perRow, setPerRow] = useState(3)
  const [fields, setFields] = useState<string[]>(DEFAULT_REPORT_FIELDS)
  const [jobId, setJobId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pollFailed, setPollFailed] = useState(false)
  const openRef = useRef(open)
  openRef.current = open

  const { job, startPolling, stopPolling } = useJobProgress({
    pollInterval: 1500,
    maxPollDuration: POLL_MAX_MS,
    onError: () => setPollFailed(true),
  })
  const [create, { loading: creating }] = useMutation(CREATE_PHOTO_REPORT, { context: { handlesErrors: true } })

  useEffect(() => {
    if (open) {
      setSource(selectedIds.length > 0 ? 'selection' : 'filter')
      setPollFailed(false)
      setTitle(t('Photos_report_default_title'))
      setPerRow(3)
      setFields(DEFAULT_REPORT_FIELDS)
      setJobId(null)
      setError(null)
    } else {
      stopPolling()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectionTooMany = selectedIds.length > REPORT_MAX_PHOTOS
  const filterTooMany = matchCount !== null && matchCount > REPORT_MAX_PHOTOS
  const sourceTooMany = source === 'selection' ? selectionTooMany : filterTooMany
  const activeJob = jobId && job && job.id === jobId ? job : null
  const running = creating || (!pollFailed && !!jobId && (!activeJob || activeJob.status === 'PENDING' || activeJob.status === 'PROCESSING'))
  const terminal = !!activeJob && ['COMPLETED', 'FAILED', 'CANCELLED'].includes(activeJob.status)

  // The hook never times out when the job is not found (null result); fail the dialog ourselves.
  useEffect(() => {
    if (!open || !jobId || terminal) return undefined
    const timer = setTimeout(() => {
      stopPolling()
      setPollFailed(true)
    }, POLL_MAX_MS)
    return () => clearTimeout(timer)
  }, [open, jobId, terminal]) // eslint-disable-line react-hooks/exhaustive-deps

  const failed = pollFailed || activeJob?.status === 'FAILED' || activeJob?.status === 'CANCELLED'
  const canGenerate = !running && !sourceTooMany && fields.length > 0 && title.trim().length > 0 && (source === 'filter' ? matchCount !== null : selectedIds.length > 0)

  const toggleField = (key: string, on: boolean) => setFields(REPORT_FIELDS.map((f) => f.key).filter((k) => (k === key ? on : fields.includes(k))))

  const generate = async () => {
    if (!canGenerate) return
    setError(null)
    setPollFailed(false)
    setJobId(null)
    try {
      const res = await create({
        variables: {
          projectId,
          input: reportInput({ source, photoIds: selectedIds, filter, title, perRow, fields, language: reportLanguage(i18n.language) }),
        },
      })
      const id = res.data?.createPhotoReport?.id
      if (id && openRef.current) {
        setJobId(id)
        startPolling(id)
      }
    } catch (e: any) {
      setError(describePhotosError(photosErrorKey(e), t))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('Photos_report_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <RadioGroup value={source} onValueChange={(v) => setSource(v as ReportSource)} disabled={running}>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="selection" id="photo-report-selection" disabled={selectedIds.length === 0 || running} />
              <Label htmlFor="photo-report-selection">{t('Photos_report_source_selection', { count: selectedIds.length })}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="filter" id="photo-report-filter" disabled={matchCount === null || filterTooMany || running} />
              <Label htmlFor="photo-report-filter">{matchCount === null ? t('Photos_report_source_filter_plain') : t('Photos_report_source_filter', { count: matchCount })}</Label>
            </div>
          </RadioGroup>
          {sourceTooMany && <p className="text-sm text-destructive">{t('Photos_report_too_many', { max: REPORT_MAX_PHOTOS })}</p>}

          <div className="space-y-2">
            <Label htmlFor="photo-report-title">{t('Photos_report_name')}</Label>
            <Input id="photo-report-title" value={title} maxLength={200} disabled={running} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>{t('Photos_report_per_row')}</Label>
            <Select value={String(perRow)} onValueChange={(v) => setPerRow(Number(v))} disabled={running}>
              <SelectTrigger className="h-9 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PER_ROW.map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('Photos_report_fields')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {REPORT_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <Checkbox id={`photo-report-field-${f.key}`} checked={fields.includes(f.key)} disabled={running} onCheckedChange={(c) => toggleField(f.key, c === true)} />
                  <Label htmlFor={`photo-report-field-${f.key}`} className="font-normal">{t(f.labelKey)}</Label>
                </div>
              ))}
            </div>
            {fields.length === 0 && <p className="text-sm text-destructive">{t('Photos_report_fields_required')}</p>}
          </div>

          {running && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
                <span>{t('Photos_report_generating')}</span>
                <span className="ml-auto text-muted-foreground">{activeJob?.progress ?? 0}%</span>
              </div>
              <Progress value={activeJob?.progress ?? 0} />
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {failed && <p className="text-sm text-destructive">{t('Photos_report_failed')}</p>}
          {activeJob?.status === 'COMPLETED' && (
            <div className="flex items-center justify-between gap-2 rounded-md border p-3">
              <span className="text-sm font-medium">{t('Photos_report_ready')}</span>
              {activeJob.pdfUrl ? (
                <Button size="sm" onClick={() => window.open(activeJob.pdfUrl as string, '_blank')}>
                  <Download className="h-4 w-4" />
                  {t('Photos_report_download')}
                </Button>
              ) : (
                <span className="text-sm text-muted-foreground">{t('Photos_report_no_link')}</span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('Common_close')}</Button>
          <Button onClick={generate} disabled={!canGenerate}>
            {creating ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {t('Photos_report_generate')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PhotoReportDialog
